// Recruiting — "Add candidate". Same write discipline as CandidateActions.tsx
// (useWrite for busy/error, authedWrite's per-call Idempotency-Key via
// createCandidate). A 409 (already on file) does not render its own message
// here — it hands the id and the doNotContact flag to `onExisting` and lets
// the board decide what that looks like, exactly as the build brief asks.
import type { JSX } from 'preact';
import { useState } from 'preact/hooks';
import { LOCATION_ORDER, LOCATION_NAMES, type LocationCode } from '../../../lib/locations';
import { useWrite } from '../../MoneyForms';
import { type Candidate, createCandidate, existingCandidateConflict } from '../../../lib/recruitingWrite';
import {
  type RecruitingViewer, POSITIONS, POSITION_LABEL, SOURCES, SOURCE_LABEL,
} from './constants';

export function AddCandidateForm(
  {
    viewer, defaultLocationCode, onCreated, onExisting,
  }: {
    viewer: RecruitingViewer;
    defaultLocationCode: LocationCode | null;
    onCreated: (candidate: Candidate) => void;
    onExisting: (existingCandidateId: number, doNotContact: boolean) => void;
  },
): JSX.Element {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [position, setPosition] = useState('');
  const [homeLocationCode, setHomeLocationCode] = useState<string>(defaultLocationCode ?? '');
  const [source, setSource] = useState('');
  const [note, setNote] = useState('');
  const write = useWrite();

  // A MANAGER may only ever file a candidate under their own clinic — same
  // restriction, same reasoning, as CandidateActions.tsx's EditForm.
  const clinicOptions: readonly LocationCode[] = viewer.role === 'MANAGER' && viewer.locationCode
    ? [viewer.locationCode]
    : LOCATION_ORDER;
  const canSave = firstName.trim() !== '' && lastName.trim() !== '';

  async function save(): Promise<void> {
    if (!canSave) return;
    let created: Candidate | null = null;
    await write.run(async () => {
      try {
        const { candidate } = await createCandidate({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: phone.trim() || undefined,
          email: email.trim() || undefined,
          licenseNumber: licenseNumber.trim() || undefined,
          position: position || undefined,
          homeLocationCode: homeLocationCode || undefined,
          source: source || undefined,
          note: note.trim() || undefined,
        });
        created = candidate;
      } catch (err) {
        const conflict = existingCandidateConflict(err);
        if (conflict) { onExisting(conflict.existingCandidateId, conflict.doNotContact); return; }
        throw err;
      }
    });
    if (created) onCreated(created);
  }

  return (
    <div data-testid="add-candidate-form">
      <div class="money-add">
        <label class="nh-field">
          <span class="nh-label">First name</span>
          <input
            type="text" value={firstName} placeholder="First name" aria-label="First name" disabled={write.busy}
            onInput={(e: Event) => setFirstName((e.target as HTMLInputElement).value)}
          />
        </label>
        <label class="nh-field">
          <span class="nh-label">Last name</span>
          <input
            type="text" value={lastName} placeholder="Last name" aria-label="Last name" disabled={write.busy}
            onInput={(e: Event) => setLastName((e.target as HTMLInputElement).value)}
          />
        </label>
        <label class="nh-field">
          <span class="nh-label">Phone</span>
          <input
            type="text" value={phone} placeholder="Phone" aria-label="Phone" disabled={write.busy}
            onInput={(e: Event) => setPhone((e.target as HTMLInputElement).value)}
          />
        </label>
        <label class="nh-field">
          <span class="nh-label">Email</span>
          <input
            type="email" value={email} placeholder="Email" aria-label="Email" disabled={write.busy}
            onInput={(e: Event) => setEmail((e.target as HTMLInputElement).value)}
          />
        </label>
        <label class="nh-field">
          <span class="nh-label">License number</span>
          <input
            type="text" value={licenseNumber} placeholder="License number" aria-label="License number" disabled={write.busy}
            onInput={(e: Event) => setLicenseNumber((e.target as HTMLInputElement).value)}
          />
        </label>
      </div>
      <div class="money-add">
        <label class="nh-field">
          <span class="nh-label">Position</span>
          <select value={position} aria-label="Position" disabled={write.busy} onChange={(e: Event) => setPosition((e.target as HTMLSelectElement).value)}>
            <option value="">Position…</option>
            {POSITIONS.map((p) => <option key={p} value={p}>{POSITION_LABEL[p]}</option>)}
          </select>
        </label>
        <label class="nh-field">
          <span class="nh-label">Clinic</span>
          <select
            value={homeLocationCode} aria-label="Clinic" disabled={write.busy}
            onChange={(e: Event) => setHomeLocationCode((e.target as HTMLSelectElement).value)}
          >
            <option value="">Clinic…</option>
            {clinicOptions.map((code) => <option key={code} value={code}>{LOCATION_NAMES[code]}</option>)}
          </select>
        </label>
        <label class="nh-field">
          <span class="nh-label">Source</span>
          <select value={source} aria-label="Source" disabled={write.busy} onChange={(e: Event) => setSource((e.target as HTMLSelectElement).value)}>
            <option value="">Source…</option>
            {SOURCES.map((s) => <option key={s} value={s}>{SOURCE_LABEL[s]}</option>)}
          </select>
        </label>
      </div>
      <label class="nh-field">
        <span class="nh-label">Note</span>
        <textarea
          class="notes-entry" rows={4} aria-label="Note" value={note} disabled={write.busy}
          onInput={(e: Event) => setNote((e.target as HTMLTextAreaElement).value)}
        />
      </label>
      <div class="money-add">
        <button
          type="button" class="btn primary" disabled={write.busy || !canSave}
          onClick={() => void save()} data-testid="add-candidate-save"
        >
          {write.busy ? 'Saving…' : 'Save'}
        </button>
      </div>
      {write.error ? <small class="bad" role="alert">{write.error}</small> : null}
    </div>
  );
}
